<template>
	<div>
		<div>
			<h2 class="mb-4 font-bold">{{ type }} {{ $t('bono') }}</h2>

			<div class="mb-4">
				<label class="block mb-2" for="title">{{ $t('title') }}</label>
				<input
					type="text"
					v-model="bonus.title"
					id="title"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				/>
				<div v-if="$v.bonus.title.$dirty">
					<div
						class="text-sm text-red-500 mt-2"
						v-if="!$v.bonus.title.required"
					>
						{{ $t('title_required') }}
					</div>
					<div
						class="text-sm text-red-500 mt-2"
						v-if="!$v.bonus.title.maxLength"
					>
						{{ $t('title_validation') }}
					</div>
				</div>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="currentcountry">País</label>
				<select
					ref="currentcountry"
					id="currentcountry"
					v-model="bonus.country_id"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				>
					<option
						v-for="(country, index) in countries"
						:value="country.id"
						:key="index"
					>
						{{ country.name }}
					</option>
				</select>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="companies">{{ $t('Empresa') }}</label>
				<select
					v-model="bonus.company_id"
					id="companies"
					class="
						w-full
						border-t-0
						border-r-0
						border-l-0
						border-b
						border-solid
						border-inputGrey
						outline-none
					"
				>
					<option
						v-for="(company, index) in companies"
						:value="company.id"
						:key="index"
					>
						{{ company.company_name }}
					</option>
				</select>
				<div v-if="$v.bonus.company_id.$dirty">
					<div
						class="text-sm text-red-500 mt-2"
						v-if="!$v.bonus.company_id.required"
					>
						{{ $t('company_required') }}
					</div>
				</div>
			</div>

			<div class="mb-4">
				<label class="block mb-2" for="body">{{ $t('body') }}</label>
				<quill-editor
					class="editor-example bubble h-40 mb-16"
					id="body"
					v-model="bonus.body"
					:content="bonus.body"
					:options="editorOption"
				>
				</quill-editor>
				<div v-if="$v.bonus.body.$dirty">
					<div class="text-sm text-red-500 mt-2" v-if="!$v.bonus.body.required">
						{{ $t('body_required') }}
					</div>
				</div>
			</div>
			<h3 class="max-w-md" v-if="image || bonus.image">
				{{ (image && image.name) || (bonus.image && bonus.image.file_name) }}
			</h3>
			<img-upload
				:image="image"
				@upload-image="img_upload"
				@reset-image="img_reset"
			/>
			<div v-if="$v.image.$dirty">
				<div class="text-sm text-red-500 mt-2" v-if="!$v.image">
					{{ $t('image_validation') }}
				</div>
			</div>
			<label>{{ $t('validity') }}</label>
			<div class="mb-4 mt-3 flex justify-around">
				<div>
					<label class="block mb-2">{{ $t('dateFrom') }}</label>
					<datepicker
						v-model="bonus.valid_from"
						:full-month-name="true"
						:language="es"
						:inline="true"
						:monday-first="true"
						:disabled-dates="disabledDates"
					>
					</datepicker>
					<div v-if="$v.bonus.valid_from.$dirty">
						<div
							class="text-sm text-red-500 mt-2"
							v-if="!$v.bonus.valid_from.required"
						>
							{{ $t('dateFrom_require') }}
						</div>
					</div>
				</div>
				<div>
					<label class="block mb-2">{{ $t('dateTo') }}</label>
					<datepicker
						v-model="bonus.valid_until"
						:full-month-name="true"
						:language="es"
						:inline="true"
						:monday-first="true"
						:disabled-dates="disabledDates"
					>
					</datepicker>
					<div v-if="$v.bonus.valid_until.$dirty">
						<div
							class="text-sm text-red-500 mt-2"
							v-if="!$v.bonus.valid_until.required"
						>
							{{ $t('dateTo_require') }}
						</div>
					</div>
				</div>
			</div>
			<div v-if="bonus.valid_from > bonus.valid_until" class="text-center mb-4">
				<div class="text-sm text-red-500 mt-2">
					{{ $t('validity_date_validation') }}
				</div>
			</div>
			<label v-if="DualListOptions">{{ $t('Roles') }}</label>
			<dual-list
				v-if="DualListOptions"
				id="vueduallist"
				:options="DualListOptions"
			/>
			<div class="mt-4 flex justify-end">
				<ButtonBlack
					@click.native="submit"
					:disabled="bonus.valid_from > bonus.valid_until"
				>
					<div v-if="loading">
						<pulse-loader
							:loading="loading"
							:color="'#fff'"
							:size="'10px'"
						></pulse-loader>
					</div>
					<div v-else>
						{{ buton_text }}
					</div>
				</ButtonBlack>
			</div>
			<div v-if="error" class="mt-4">
				<p class="text-sm">{{ $t('server_error') }}</p>
				<p class="text-sm text-red-500" v-html="server_error"></p>
			</div>
		</div>
	</div>
</template>

<script>
import datepicker from 'vuejs-datepicker'
import { es } from 'vuejs-datepicker/dist/locale'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import DualList from '@/components/DualList.vue'
import { required, maxLength } from 'vuelidate/lib/validators'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { single, uploadImage } from '@/mixins'
import ImgUpload from '@/components/ImgUpload.vue'
import { fetchCountriesSelect } from '@/apollo/countries'
import { fetchCompaniesSelect } from '@/apollo/companies'
import { fetchRolesSelect } from '@/apollo/roles'
import { fetchBonus, updateBonus, insertBonus } from '@/apollo/bonuses'

export default {
	mixins: [single, uploadImage],
	components: {
		quillEditor,
		datepicker,
		DualList,
		PulseLoader,
		ImgUpload
	},
	data() {
		return {
			disabledDates: {
				to: this.id ? null : this.$moment().subtract(1, 'days').toDate()
			},
			bonus: {
				title: '',
				country_id: '',
				company_id: '',
				body: '',
				roles: this.id ? null : [],
				valid_from: '',
				valid_until: '',
				image: null
			},
			roles: null,
			countries: [],
			companies: [],
			es: es,
			editorOption: {
				theme: 'snow',
				placeholder: '',
				modules: {
					toolbar: [
						['bold', 'italic', 'underline', 'strike'],
						[{ list: 'ordered' }, { list: 'bullet' }],
						[{ header: [1, 2, 3, 4, 5, 6, false] }],
						[{ color: [] }, { background: [] }],
						[{ font: [] }],
						[{ align: [] }],
						['link'],
						['clean']
					]
				}
			},
			image: null
		}
	},
	apollo: {
		roles: fetchRolesSelect(),
		countries: fetchCountriesSelect(),
		companies: fetchCompaniesSelect(),
		bonus() {
			const query = fetchBonus(this.id)
			query.skip = this.id == null
			return query
		}
	},
	watch: {
		roles: function (data) {
			if (this.bonus.roles !== null && this.DualListOptions === null) {
				this.loadRolesList(data, this.bonus.roles)
			}
		},
		bonus: function (data) {
			if (
				this.id != null &&
				this.roles !== null &&
				this.DualListOptions === null
			) {
				this.loadRolesList(this.roles, data.roles)
			}
		}
	},
	methods: {
		img_reset() {
			this.image ? (this.image = null) : (this.bonus.image = null)
		},
		img_upload(img) {
			this.image = img
		},
		loadRolesList(roles, selected) {
			this.loadDualList(
				roles,
				selected
					? selected
							.map(org => roles.find(elem => elem.id == org.id))
							.filter(elem => elem != null)
					: selected
			)
		},
		async updateSingleBonus() {
			if (this.image)
				this.bonus.image = await this.uploadImage(
					this.bonus,
					'bonuses',
					this.image
				)
			const mutationData = {
				bonusId: this.id,
				bonusData: this.getBonusUpdateData(),
				bonusRoles: this.DualListOptions.selectedItems
			}
			await this.$apollo.mutate(updateBonus(mutationData))
		},

		async insertSingleBonus() {
			this.bonus.image = await this.uploadImage(
				this.bonus,
				'bonuses',
				this.image
			)
			const mutationData = {
				bonusData: this.getBonusInsertData(),
				bonusRoles: this.DualListOptions.selectedItems
			}
			await this.$apollo.mutate(insertBonus(mutationData))
		},

		getBonusUpdateData() {
			return {
				country_id: this.bonus.country_id,
				title: this.bonus.title,
				company_id: this.bonus.company_id,
				body: this.bonus.body,
				valid_from: this.bonus.valid_from,
				valid_until: this.bonus.valid_until,
				image_id: this.bonus.image ? this.bonus.image.id : null
			}
		},

		getBonusInsertData() {
			return {
				country_id: this.bonus.country_id,
				title: this.bonus.title,
				company_id: this.bonus.company_id,
				body: this.bonus.body,
				valid_from: this.bonus.valid_from,
				valid_until: this.bonus.valid_until,
				image: this.bonus.image ? { data: this.bonus.image } : undefined
			}
		},

		async submit() {
			this.startLoadingState()
			this.$v.$touch()
			if (!this.$v.$invalid) {
				;(this.id ? this.updateSingleBonus() : this.insertSingleBonus())
					.then(() => {
						this.stopLoadingState()
						this.$alert("Tu Bono está actualizado", "¡Enhorabuena!", "success")
						})
					.catch(error => {
						this.$alert( error , "Error", "error")
						this.setError(error)
					})
			} else {
				this.$alert(this.$t('form_validation'), "Advertencia", "warning")
				this.setError(this.$t('form_validation'))
			}
		}
	},
	validations() {
		return {
			image: function (img) {
				return img || this.bonus.image
			},
			bonus: {
				title: {
					required,
					maxLength: maxLength(100)
				},
				company_id: {
					required
				},
				body: {
					required
				},
				valid_from: {
					required
				},
				valid_until: {
					required
				}
			}
		}
	}
}
</script>
